import { useEffect, useState } from "react";
import api, { formatDate } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { PageHeader, StatusBadge, EmptyState, useConfirm } from "../../components/common";
import { WARRANTY_STATUS } from "../../lib/channels";
import { Plus, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = { customer_name: "", customer_phone: "", job_number: "", device: "", issue: "", warranty_until: "", note: "", status: "acik" };

export default function Warranty() {
  const [ask, confirmEl] = useConfirm();
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState(null); // null = kapalı

  const load = () => api.get("/warranty-claims").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.customer_name || !form.issue) { toast.error("Müşteri adı ve sorun açıklaması zorunlu"); return; }
    try {
      if (form.id) await api.put(`/warranty-claims/${form.id}`, form);
      else await api.post("/warranty-claims", form);
      toast.success("Kaydedildi");
      setForm(null); load();
    } catch { toast.error("Kaydedilemedi"); }
  };
  const setStatus = async (w, status) => { await api.put(`/warranty-claims/${w.id}`, { status }); load(); };
  const remove = async (w) => { if (await ask("Garanti talebi silinsin mi?")) { await api.delete(`/warranty-claims/${w.id}`); load(); } };
  const shown = list.filter((w) => filter === "all" || w.status === filter);

  return (
    <div className="space-y-6" data-testid="warranty-page">
      <PageHeader eyebrow="Müşteri Hizmetleri" title="Garanti Talepleri" subtitle="Yapılan işlem veya parça garantisi kapsamındaki müşteri taleplerini takip edin.">
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setForm({ ...empty })} data-testid="new-warranty"><Plus className="h-4 w-4 mr-2" />Yeni Talep</Button>
      </PageHeader>
      <div className="flex flex-wrap gap-2">
        {["all", ...Object.keys(WARRANTY_STATUS)].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full text-sm font-medium border ${filter === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
            {s === "all" ? "Tümü" : WARRANTY_STATUS[s].label} <span className="opacity-60 ml-1">{s === "all" ? list.length : list.filter((w) => w.status === s).length}</span>
          </button>
        ))}
      </div>
      <Card className="border border-slate-200 shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="warranty-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-3">Müşteri</th><th className="px-4 py-3">Servis No / Cihaz</th><th className="px-4 py-3">Sorun</th><th className="px-4 py-3">Garanti Bitiş</th><th className="px-4 py-3">Durum</th><th className="px-4 py-3 w-12" /></tr>
            </thead>
            <tbody>
              {shown.length === 0 && <tr><td colSpan={6}><EmptyState icon={ShieldCheck} title="Garanti talebi yok" /></td></tr>}
              {shown.map((w) => (
                <tr key={w.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="px-4 py-3 cursor-pointer" onClick={() => setForm(w)}><div className="font-semibold text-blue-700">{w.customer_name}</div><div className="text-xs text-slate-500">{w.customer_phone}</div></td>
                  <td className="px-4 py-3"><div className="font-mono text-xs">{w.job_number || "-"}</div><div className="text-xs text-slate-500">{w.device}</div></td>
                  <td className="px-4 py-3 max-w-xs truncate text-slate-700" title={w.issue}>{w.issue}</td>
                  <td className="px-4 py-3 text-slate-500">{w.warranty_until ? formatDate(w.warranty_until) : "-"}</td>
                  <td className="px-4 py-3">
                    <Select value={w.status} onValueChange={(v) => setStatus(w, v)}>
                      <SelectTrigger className="h-8 w-36"><StatusBadge map={WARRANTY_STATUS} value={w.status} /></SelectTrigger>
                      <SelectContent>{Object.entries(WARRANTY_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3"><button onClick={() => remove(w)} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent>
          {form && (<>
            <DialogHeader><DialogTitle>{form.id ? "Garanti Talebi" : "Yeni Garanti Talebi"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Müşteri *</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="w-name" /></div>
                <div><Label>Telefon</Label><Input value={form.customer_phone || ""} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Servis No</Label><Input placeholder="SRV-1234567" value={form.job_number || ""} onChange={(e) => setForm({ ...form, job_number: e.target.value })} /></div>
                <div><Label>Garanti bitiş</Label><Input type="date" value={form.warranty_until || ""} onChange={(e) => setForm({ ...form, warranty_until: e.target.value })} /></div>
              </div>
              <div><Label>Cihaz</Label><Input value={form.device || ""} onChange={(e) => setForm({ ...form, device: e.target.value })} /></div>
              <div><Label>Sorun *</Label><Textarea rows={3} value={form.issue} onChange={(e) => setForm({ ...form, issue: e.target.value })} data-testid="w-issue" /></div>
              <div><Label>Not</Label><Textarea rows={2} value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setForm(null)}>Vazgeç</Button><Button className="bg-blue-600 hover:bg-blue-700" onClick={save} data-testid="w-save">Kaydet</Button></DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>
      {confirmEl}
    </div>
  );
}
